/**
 * Imports
 */
import { createSelector } from '@reduxjs/toolkit';
import { filter } from 'lodash';
import { VibrantlyState } from 'store/types';
import type { Firebase } from 'types/firebase';

/**
 * Selectors
 */
export const rootSelector = (state: VibrantlyState) => state.merchants;

export const showAddOwnerModalSelector = createSelector(
  [rootSelector],
  (root) => root.modals.ownerModal,
);

export const merchantsSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (firestore) => firestore.ordered.merchants,
);

export const merchantSelector = (id: string) =>
  createSelector(
    [firestoreRootSelector],
    (firestore) => firestore.data[`merchant_${id}`] as Firebase.Merchant,
  );
export const accountSelector = (id: string) =>
  createSelector(
    [firestoreRootSelector],
    (firestore) => firestore.data[`account_${id}`] as Firebase.ApiAccount,
  );
export const maxNumberOfTerminalsSelector = (id: string) =>
  createSelector(
    [accountSelector(id)],
    (account) => account?.settings?.maxNumberOfTerminals,
  );
export const firestoreRootSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (root) => root,
);
export const currentMerchantSelector = createSelector(
  [firestoreRootSelector],
  (firestore) => firestore.data.merchant as Firebase.Merchant,
);

export const merchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => merchants?.length,
);
export const completedMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants: Record<number, Firebase.Merchant>) => {
    return filter(
      merchants,
      (merchant) =>
        merchant.clearhausKYCdone != null &&
        merchant.readyForApplication != null,
    ).length;
  },
);
export const pendingVibrantMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => {
    return filter(merchants, (merchant) => merchant.readyForApplication == null)
      .length;
  },
);
export const pendingClearhausMerchantsCountSelector = createSelector(
  [merchantsSelector],
  (merchants) => {
    return filter(merchants, (merchant) => merchant.clearhausKYCdone == null)
      ?.length;
  },
);
export const devicesSelector = createSelector(
  (state: VibrantlyState) => state.firestore,
  (firestore) => firestore?.data?.devices as Record<string, Firebase.Device>,
);
/**
 * Firestore
 */
export const partnerNameSelector = createSelector(
  [firestoreRootSelector],
  (root) => root.data?.partnerAccount?.name,
);
export const partnersSelector = createSelector(
  [firestoreRootSelector],
  (root) => root.data?.partners as Record<string, Firebase.Partner>,
);
export const partnerSelector = (partnerId?: string) =>
  createSelector([partnersSelector], (partners) => {
    if (partnerId) {
      return partners[partnerId];
    } else {
      return null;
    }
  });

/**
 * Filters
 */
export const filtersSelector = createSelector(
  [rootSelector],
  (root) => root.filters,
);
